import { useCallback, useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";

type State = { perRoom: Record<string, number>; total: number };

let cachedUserId: string | null = null;
let cachedState: State = { perRoom: {}, total: 0 };
let unreadChannel: ReturnType<typeof supabase.channel> | null = null;
let recomputeTimer: ReturnType<typeof setTimeout> | null = null;
let recomputeInFlight = false;
let recomputeAgain = false;
let pendingRecomputeUserId: string | null = null;
let generation = 0;
const stateListeners = new Set<(state: State) => void>();
const lastReadUpdateByRoom = new Map<string, number>();
const STORAGE_PREFIX = "almunajah-unread-cache:";

function readStoredState(userId: string | null): State | null {
  if (!userId || typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(`${STORAGE_PREFIX}${userId}`);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as State;
    if (!parsed || typeof parsed.total !== "number" || typeof parsed.perRoom !== "object") return null;
    return parsed;
  } catch {
    return null;
  }
}

function writeStoredState(userId: string | null, state: State) {
  if (!userId || typeof window === "undefined") return;
  try {
    window.localStorage.setItem(`${STORAGE_PREFIX}${userId}`, JSON.stringify(state));
  } catch { /* ignore */ }
}

function publishState(next: State) {
  cachedState = next;
  writeStoredState(cachedUserId, next);
  stateListeners.forEach((listener) => listener(next));
}

export function primeUnreadChatsCache(userId: string | null | undefined) {
  const nextUserId = userId ?? null;
  const userChanged = cachedUserId !== nextUserId;
  if (userChanged && unreadChannel) {
    supabase.removeChannel(unreadChannel);
    unreadChannel = null;
  }
  cachedUserId = nextUserId;
  const stored = readStoredState(nextUserId);
  cachedState = stored ?? { perRoom: {}, total: 0 };
  stateListeners.forEach((listener) => listener(cachedState));
}

export function resetUnreadChatsCache() {
  generation += 1;
  cachedUserId = null;
  cachedState = { perRoom: {}, total: 0 };
  lastReadUpdateByRoom.clear();
  if (recomputeTimer) {
    clearTimeout(recomputeTimer);
    recomputeTimer = null;
  }
  recomputeAgain = false;
  pendingRecomputeUserId = null;
  if (unreadChannel) {
    supabase.removeChannel(unreadChannel);
    unreadChannel = null;
  }
  stateListeners.forEach((listener) => listener(cachedState));
}

export function useUnreadChats() {
  const [me, setMe] = useState<string | null>(null);
  const [state, setState] = useState<State>(cachedState);

  useEffect(() => {
    let cancelled = false;
    const loadUser = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!cancelled) setMe(user?.id ?? null);
    };
    void loadUser();
    const { data: sub } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === "SIGNED_OUT") {
        resetUnreadChatsCache();
        setMe(null);
        return;
      }
      if (event === "SIGNED_IN" || event === "USER_UPDATED") {
        const nextUserId = session?.user?.id ?? null;
        if (nextUserId !== cachedUserId) primeUnreadChatsCache(nextUserId);
        setMe(nextUserId);
      }
    });
    return () => {
      cancelled = true;
      sub.subscription.unsubscribe();
    };
  }, []);

  const recompute = useCallback((userId: string) => recomputeUnreadState(userId), []);

  useEffect(() => {
    if (!me) return;
    const listener = (next: State) => setState(next);
    stateListeners.add(listener);

    if (cachedUserId !== me) {
      primeUnreadChatsCache(me);
      if (unreadChannel) {
        supabase.removeChannel(unreadChannel);
        unreadChannel = null;
      }
    }

    recompute(me);

    if (!unreadChannel) {
      const channelName = `unread_chats_${me}`;
      unreadChannel = supabase.channel(channelName);
      unreadChannel.on(
        "postgres_changes",
        { event: "INSERT", schema: "public", table: "chat_messages" },
        () => scheduleRecompute(me),
      );
      unreadChannel.on(
        "postgres_changes",
        { event: "UPDATE", schema: "public", table: "chat_room_members", filter: `user_id=eq.${me}` },
        () => scheduleRecompute(me),
      );
      unreadChannel.on(
        "postgres_changes",
        { event: "INSERT", schema: "public", table: "chat_room_members", filter: `user_id=eq.${me}` },
        () => scheduleRecompute(me),
      );
      unreadChannel.on(
        "postgres_changes",
        { event: "DELETE", schema: "public", table: "chat_messages" },
        () => scheduleRecompute(me),
      );
      unreadChannel.subscribe();
    }

    return () => { stateListeners.delete(listener); };
  }, [me, recompute]);

  return state;
}

function scheduleRecompute(userId: string) {
  if (recomputeTimer) clearTimeout(recomputeTimer);
  recomputeTimer = setTimeout(() => {
    recomputeTimer = null;
    recomputeUnreadState(userId);
  }, 450);
}

async function recomputeUnreadState(userId: string) {
  const runGeneration = generation;
  if (recomputeInFlight) {
    recomputeAgain = true;
    pendingRecomputeUserId = userId;
    return;
  }
  recomputeInFlight = true;
  try {
    const { data: members } = await supabase
      .from("chat_room_members")
      .select("room_id, last_read_at")
      .eq("user_id", userId);
    if (!members || members.length === 0) {
      publishState({ perRoom: {}, total: 0 });
      return;
    }
    const roomIds = members.map((m) => m.room_id);
    const lastRead: Record<string, string> = {};
    for (const m of members) lastRead[m.room_id] = m.last_read_at;

    const { data: msgs } = await supabase
      .from("chat_messages")
      .select("room_id, user_id, created_at")
      .in("room_id", roomIds)
      .is("deleted_at", null)
      .neq("user_id", userId)
      .order("created_at", { ascending: false })
      .limit(1000);

    const perRoom: Record<string, number> = {};
    for (const m of msgs ?? []) {
      const lr = lastRead[m.room_id];
      if (!lr || new Date(m.created_at) > new Date(lr)) {
        perRoom[m.room_id] = (perRoom[m.room_id] ?? 0) + 1;
      }
    }
    if (runGeneration === generation && cachedUserId === userId) {
      publishState({ perRoom, total: Object.values(perRoom).reduce((a, b) => a + b, 0) });
    }
  } finally {
    recomputeInFlight = false;
    if (recomputeAgain) {
      const nextUserId = pendingRecomputeUserId ?? userId;
      recomputeAgain = false;
      pendingRecomputeUserId = null;
      scheduleRecompute(nextUserId);
    }
  }
}

export async function markRoomRead(roomId: string) {
  const now = Date.now();
  const last = lastReadUpdateByRoom.get(roomId) ?? 0;
  if (now - last < 5000) return;
  lastReadUpdateByRoom.set(roomId, now);
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return;
  await supabase
    .from("chat_room_members")
    .update({ last_read_at: new Date().toISOString() })
    .eq("room_id", roomId)
    .eq("user_id", user.id);
}
